import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { finalize, Observable, of, switchMap } from 'rxjs';

import { AttestationDataService } from '@sondermind/data-access/attestation';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { Logger } from '@sondermind/datadog-logging';
import { AccountSetupService } from '@sondermind/utilities/services';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

/** Where the card is hosted; decides who refreshes the current user after signing. */
export enum FinancialAgreementTaskCardContext {
  /** Homepage: the card reloads the current user itself so the card and other setup prompts clear. */
  HOME = 'home',
  /** Account setup: the host reloads the user on `signed`, as it does after every setup step. */
  ACCOUNT_SETUP = 'account_setup'
}

const SIGN_ERROR_COPY = 'We couldn\'t record your agreement. Please try again.';

/**
 * Urgent task card for a client who has a payment card on file but has never signed the Financial
 * Responsibility Agreement. Signs the existing unsigned agreement, or creates and signs one when none
 * exists. The host decides visibility (flag + `hasCreditCard && !hasSignedFinancialAgreement`).
 */
@Component({
  selector: 'financial-agreement-task-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './financial-agreement-task-card.component.html',
  styleUrl: './financial-agreement-task-card.component.scss',
  imports: [ReactiveFormsModule, IrisCheckboxModule, IrisIconComponent]
})
export class FinancialAgreementTaskCardComponent {
  readonly context = input<FinancialAgreementTaskCardContext>(FinancialAgreementTaskCardContext.HOME);

  readonly signed = output<void>();

  private readonly attestationDataService = inject(AttestationDataService);
  private readonly currentUser = inject(CurrentUser);
  private readonly accountSetupService = inject(AccountSetupService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly agreementUrl = InfoLinks.FINANCIAL_RESPONSIBILITY_AGREEMENT;
  protected readonly iconSizes = IconSizes;

  protected readonly agreedControl = new FormControl<boolean>(false, { nonNullable: true });
  private readonly agreed = toSignal(this.agreedControl.valueChanges, { initialValue: false });

  protected readonly submitting = signal(false);
  protected readonly errorCopy = signal<string | null>(null);

  protected readonly canSubmit = computed(() => this.agreed() && !this.submitting());

  sign(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.submitting.set(true);
    this.errorCopy.set(null);

    // Forwarded so the account-completion job attributes the completion to the setup flow, if any.
    this.attestationDataService.signFinancialAgreement$(this.accountSetupService.context ?? null).pipe(
      switchMap(() => this.refreshUser$()),
      finalize(() => this.submitting.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => this.signed.emit(),
      error: (err: unknown) => {
        this.logger.error('Failed to sign financial responsibility agreement', { err });
        this.errorCopy.set(SIGN_ERROR_COPY);
        // The agreement may have been signed elsewhere (e.g. another tab), which 404s the update.
        // Refreshing lets the host hide the card if nothing is actually outstanding.
        this.refreshUser$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ error: () => {} });
      }
    });
  }

  private refreshUser$(): Observable<void> {
    return this.context() === FinancialAgreementTaskCardContext.HOME ? this.currentUser.reload$() : of(undefined);
  }
}
